import { Button, Grid, IconButton, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ProfilePictureUploader from '../../../components/common/ProfilePictureUploader';
import PhoneIcon from '@mui/icons-material/Phone';
import { formatPhoneNumber } from '../../../utilities/formatters';
import EmailIcon from '@mui/icons-material/Email';
import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  me,
  updateUser as updateUserMutation,
  CONTACT_METHOD_TYPE,
  MIXPANEL_EVENTS,
} from 'vendor-marketplace-shared-library';
import { UPDATE_USER } from '../../../error-messages';
import useAuth from '../../../hooks/useAuth';
import useAlert from '../../../hooks/useAlert';
import EditNameModal from './EditNameModal';
import ContactMethodUpdateModal from './ContactMethodUpdateModal';
import useMixpanel from '../../../hooks/useMixpanel';
import AddIcon from '@mui/icons-material/Add';

export default function UserUpdateForm() {
  const alert = useAlert();
  const { mixpanelTrack } = useMixpanel();
  const { userQuery, logout } = useAuth();
  const user = userQuery?.data?.me;

  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [openEditNameModal, setOpenEditNameModal] = useState(false);
  const [openEditContactMethodModal, setOpenEditContactMethodModal] = useState(false);
  const [contactMethodType, setContactMethodType] = useState<string>('');

  const [updateUser] = useMutation(updateUserMutation, {
    refetchQueries: [me],
    awaitRefetchQueries: true,
    onError: () => {
      alert.setAlert({ message: UPDATE_USER });
    },
    onCompleted: async () => {
      mixpanelTrack(MIXPANEL_EVENTS.USER_SETTINGS_UPDATE_PROFILE_PRESSED);
      setOpenEditNameModal(false);
    },
  });

  const onUpdate = async () => {
    await updateUser({
      variables: {
        input: {
          firstName,
          lastName,
        },
      },
    });
  };

  return (
    <Grid item container direction={'column'} spacing={2} sx={styles.centeredContainer}>
      {/*User profile info*/}
      <Grid item container spacing={1} sx={styles.centeredContainer}>
        <Grid item>
          <Typography>{user.fullName}</Typography>
        </Grid>
      </Grid>
      <Grid item>
        <Typography color={'secondary.main'}>{user.userRole.name}</Typography>
      </Grid>
      <Grid item>
        <ProfilePictureUploader />
      </Grid>
      <Grid item>
        <Button
          size={'small'}
          variant={'outlined'}
          onClick={() => {
            setOpenEditNameModal(true);
            mixpanelTrack(MIXPANEL_EVENTS.USER_SETTINGS_UPDATE_PROFILE_PRESSED);
          }}
        >
          Edit User Info
        </Button>
      </Grid>
      {/*Phone and edit button*/}
      <Grid item container spacing={1} sx={styles.centeredContainer}>
        <Grid item>
          <PhoneIcon />
        </Grid>
        <Grid item>
          <Typography>
            {user.phoneContactMethod?.value
              ? formatPhoneNumber(user.phoneContactMethod.value)
              : 'Add phone number'}
          </Typography>
        </Grid>
        <Grid item>
          <IconButton
            onClick={() => {
              setContactMethodType(CONTACT_METHOD_TYPE.PHONE);
              setOpenEditContactMethodModal(true);
            }}
          >
            {user.phoneContactMethod ? <EditIcon /> : <AddIcon />}
          </IconButton>
        </Grid>
      </Grid>
      {/*Email and edit button*/}
      <Grid item container spacing={1} sx={styles.centeredContainer}>
        <Grid item>
          <EmailIcon />
        </Grid>
        <Grid item>
          <Typography>{user.emailContactMethod?.value || 'Add email'}</Typography>
        </Grid>
        <Grid item>
          <IconButton
            onClick={() => {
              setContactMethodType(CONTACT_METHOD_TYPE.EMAIL);
              setOpenEditContactMethodModal(true);
            }}
          >
            {user.emailContactMethod ? <EditIcon /> : <AddIcon />}
          </IconButton>
        </Grid>
      </Grid>
      {/*User Account Edit Modal*/}
      {openEditNameModal && (
        <EditNameModal
          open={openEditNameModal}
          setOpen={setOpenEditNameModal}
          firstName={firstName}
          lastName={lastName}
          setFirstName={setFirstName}
          setLastName={setLastName}
          onUpdate={onUpdate}
        />
      )}
      {/*Contact Method Edit Modal*/}
      <ContactMethodUpdateModal
        open={openEditContactMethodModal}
        setOpen={setOpenEditContactMethodModal}
        contactMethodType={contactMethodType}
        refetchData={() => {}}
      />
    </Grid>
  );
}

const styles = {
  centeredContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  spaceBetween: {
    justifyContent: 'center',
    alignItems: 'center',
  },
};
